// FILE: src/components/feedback/FeedbackMetricsPanel.js
//
// Telemetry panel for the Admin Feedback Hub. Reads the admin-gated
// feedback_metrics(p_from, p_to) RPC and renders simple breakdowns:
// submitted this week, by category, by certification, by page, by source,
// plus most-requested features and most-reported issues. No chart library —
// lightweight bar rows keep it consistent with the rest of the admin surface.

import React, { useState, useEffect } from 'react';
import { getFeedbackMetrics } from '../../services/feedbackService';
import { styles, certLabel } from './feedbackStyles';

function isoDaysAgo(n) {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const m = {
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '18px' },
  panel: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: '10px', padding: '18px 20px' },
  title: { fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#888', fontWeight: 700, marginBottom: '14px' },
  barRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
  barLabel: { fontSize: '0.82rem', color: '#444', width: '40%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  barTrack: { flex: 1, background: '#eef0f6', borderRadius: '6px', height: '14px', overflow: 'hidden' },
  barFill: (pct) => ({ width: `${pct}%`, height: '100%', background: '#2c63ff', borderRadius: '6px' }),
  barNum: { fontSize: '0.8rem', color: '#666', width: '28px', textAlign: 'right' },
  listItem: { fontSize: '0.85rem', color: '#333', padding: '7px 0', borderBottom: '1px solid #f2f2f2', lineHeight: 1.4 },
  big: { fontSize: '2.2rem', fontWeight: 800, color: '#1a1a2e' },
};

function BarBlock({ title, obj, labeller }) {
  const entries = Object.entries(obj || {}).sort((a, b) => b[1] - a[1]);
  const max = entries.reduce((mx, [, n]) => Math.max(mx, n), 0) || 1;
  return (
    <div style={m.panel}>
      <div style={m.title}>{title}</div>
      {entries.length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem' }}>No data in range.</p>}
      {entries.map(([k, n]) => (
        <div key={k} style={m.barRow}>
          <span style={m.barLabel}>{labeller ? labeller(k) : k}</span>
          <span style={m.barTrack}><span style={m.barFill(Math.round((n / max) * 100))} /></span>
          <span style={m.barNum}>{n}</span>
        </div>
      ))}
    </div>
  );
}

export default function FeedbackMetricsPanel() {
  const [days, setDays]   = useState(30);
  const [data, setData]   = useState(null);
  const [loading, setLoad] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoad(true); setError(null);
    getFeedbackMetrics(isoDaysAgo(days), isoDaysAgo(0)).then(({ data: d, error: e }) => {
      if (cancelled) return;
      if (e) setError(e.message || 'Failed to load metrics');
      setData(d);
      setLoad(false);
    });
    return () => { cancelled = true; };
  }, [days]);

  return (
    <div>
      <div style={{ ...styles.filterBar, justifyContent: 'flex-end' }}>
        <label style={{ fontSize: '0.85rem', color: '#666' }}>Window</label>
        <select style={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
        </select>
      </div>

      {loading && <p style={{ color: '#999' }}>Loading metrics…</p>}
      {error && <p style={{ color: '#c62828' }}>{error}</p>}

      {data && !loading && (
        <>
          <div style={{ ...m.panel, marginBottom: '18px', textAlign: 'center' }}>
            <div style={m.big}>{data.submitted ?? 0}</div>
            <div style={styles.cardLabel}>Submitted in window</div>
          </div>
          <div style={m.grid}>
            <BarBlock title="By category" obj={data.by_category} />
            <BarBlock title="By certification" obj={data.by_certification} labeller={certLabel} />
            <BarBlock title="By page" obj={data.by_page} />
            <BarBlock title="By source" obj={data.by_source} />
          </div>

          <div style={{ ...m.grid, marginTop: '18px' }}>
            <div style={m.panel}>
              <div style={m.title}>Most requested features</div>
              {(data.top_features || []).length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem' }}>None in range.</p>}
              {(data.top_features || []).map((f) => (
                <div key={f.id} style={m.listItem}>{f.title || f.description}</div>
              ))}
            </div>
            <div style={m.panel}>
              <div style={m.title}>Most reported issues</div>
              {(data.top_issues || []).length === 0 && <p style={{ color: '#aaa', fontSize: '0.85rem' }}>None in range.</p>}
              {(data.top_issues || []).map((f) => (
                <div key={f.id} style={m.listItem}>{f.title || f.description}</div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
